import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppSettings, Handbook } from '@chronos/shared';
import { useAuth } from '@/auth/AuthContext';
import { apiFetch } from '@/api/client';
import { useSettings } from '@/hooks/queries';
import { Card, ConfirmButton, ErrorNote, PageHeader, Spinner, SubmitButton } from '@/components/ui';
import { Markdown } from '@/lib/markdown';
import { formatDateTime } from '@/lib/format';

/**
 * The HR handbook. Everyone reads it; admins edit it.
 *
 * The prose is editable and therefore can drift, so the concrete numbers are
 * *not* written into it — they are rendered underneath from live settings and
 * are always right, whatever the text says.
 */
export function HandbookPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('ADMIN');
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);

  const handbook = useQuery({ queryKey: ['handbook'], queryFn: () => apiFetch<Handbook>('/api/handbook') });
  const settings = useSettings();

  useEffect(() => {
    if (handbook.data && !editing) setDraft(handbook.data.content);
  }, [handbook.data, editing]);

  const save = useMutation({
    mutationFn: () => apiFetch<Handbook>('/api/handbook', { method: 'PUT', body: { content: draft } }),
    onSuccess: (next) => {
      qc.setQueryData(['handbook'], next);
      setEditing(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const reset = useMutation({
    mutationFn: () => apiFetch<Handbook>('/api/handbook/reset', { method: 'POST' }),
    onSuccess: (next) => {
      qc.setQueryData(['handbook'], next);
      setDraft(next.content);
      setEditing(false);
    },
  });

  if (handbook.isLoading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="HR Handbook"
        description="How time, attendance, leave and pay work here."
        actions={
          isAdmin && (
            <div className="flex flex-wrap items-center gap-2">
              {saved && <span className="text-sm text-emerald-700">Saved.</span>}
              {editing ? (
                <>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      setDraft(handbook.data?.content ?? '');
                      setEditing(false);
                    }}
                  >
                    Cancel
                  </button>
                  <SubmitButton pending={save.isPending} className="btn-primary" onClick={() => save.mutate()}>
                    Save
                  </SubmitButton>
                </>
              ) : (
                <>
                  <ConfirmButton
                    className="btn-ghost text-xs text-rose-600"
                    label="Restore default"
                    confirmLabel="Replace everything"
                    onConfirm={() => reset.mutate()}
                  />
                  <button className="btn-primary" onClick={() => setEditing(true)}>Edit</button>
                </>
              )}
            </div>
          )
        }
      />

      <ErrorNote error={handbook.error ?? save.error ?? reset.error} />

      {editing ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="p-4">
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-sm font-semibold text-slate-800">Markdown</h2>
              <span className="text-[11px] text-slate-400"># heading · **bold** · - list · | table |</span>
            </div>
            <textarea
              className="input min-h-[32rem] w-full font-mono text-xs leading-relaxed"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              spellCheck={false}
            />
          </Card>
          <Card className="p-5">
            <h2 className="mb-2 text-sm font-semibold text-slate-800">Preview</h2>
            <Markdown source={draft} />
          </Card>
        </div>
      ) : (
        <Card className="p-6">
          <Markdown source={handbook.data?.content ?? ''} />
        </Card>
      )}

      {!editing && settings.data && <CurrentNumbers settings={settings.data} />}

      {handbook.data?.updatedAt && (
        <p className="mt-3 text-center text-xs text-slate-400">
          Last edited {formatDateTime(handbook.data.updatedAt)}
          {handbook.data.updatedByName ? ` by ${handbook.data.updatedByName}` : ''}
        </p>
      )}
    </div>
  );
}

/**
 * The live figures behind the rules above. Read from settings on every load, so
 * these cannot fall out of step with payroll the way written-in numbers would.
 */
function CurrentNumbers({ settings: s }: { settings: AppSettings }) {
  const hours = (n: number) => `${n} hour${n === 1 ? '' : 's'}`;
  const money = (n: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: s.currency || 'INR', maximumFractionDigits: 0 }).format(n);
  const bands = [...(s.deficitLeaveBands ?? [])].sort((a, b) => a.fromHours - b.fromHours);

  return (
    <Card className="mt-4 p-6">
      <h2 className="text-lg font-bold text-slate-900">Your organisation's current numbers</h2>
      <p className="mt-1 text-sm text-slate-500">Taken live from the settings, so these are always what payroll actually uses.</p>

      <div className="mt-4 grid gap-6 sm:grid-cols-2">
        <Group title="The working day">
          <Row label="Expected work" value={hours(s.workdayHours)} />
          <Row label="Expected break" value={hours(s.breakHours)} />
          <Row label="Day starts" value={`${s.workdayStart} (late after ${s.lateGraceMinutes} min)`} />
          <Row label="Timers stop at" value={s.timerCutoffTime ?? 'no cut-off'} />
          <Row label="Half day" value={`${s.halfDayHours}–${s.halfDayMaxHours} hours`} />
          <Row label="Full day" value={`above ${s.halfDayMaxHours} hours`} />
          <Row label="Counted in" value={s.attendanceTimezone} />
          {s.attendanceStartsOn && <Row label="Tracking began" value={s.attendanceStartsOn} />}
        </Group>

        <Group title="Leave">
          <Row label="Paid leave accrued" value={`${s.paidLeavesPerMonth} day(s) per month`} />
          <Row label="A half day counts as" value={`${s.halfDayLeaveWeight} of a leave`} />
          <Row label="Casual leave notice" value={s.casualLeaveNoticeWorkingDays === 0 ? 'by the previous working day' : `${s.casualLeaveNoticeWorkingDays} working day${s.casualLeaveNoticeWorkingDays === 1 ? '' : 's'}`} />
          <Row label="Medical leave" value={s.autoApproveMedicalLeave ? 'approved automatically' : 'needs admin approval'} />
          <Row label="Leave year ends" value={new Date(2000, s.leaveYearEndMonth - 1, 1).toLocaleString(undefined, { month: 'long' })} />
          <Row label="Unused leave" value={s.encashLeavesAtYearEnd ? 'paid out at year end' : 'forfeited at year end'} />
        </Group>

        <Group title="Attendance bonus">
          <Row label="No leave" value={money(s.attendanceBonusNoLeave)} />
          <Row label="Up to one leave" value={money(s.attendanceBonusOneLeave)} />
          <Row label="More than one leave" value={money(s.attendanceBonusTwoPlusLeaves)} />
        </Group>

        <Group title="Monthly hours deficit">
          {bands.length === 0 ? (
            <p className="text-sm text-slate-500">
              No bands are configured, so short hours never become leave.
            </p>
          ) : (
            <>
              <Row label={`Under ${bands[0].fromHours} hours short`} value="no charge" />
              {bands.map((b) => (
                <Row key={b.fromHours} label={`From ${b.fromHours} hours short`} value={`${b.days} day(s) leave`} />
              ))}
            </>
          )}
        </Group>
      </div>
    </Card>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h3>
      <dl className="space-y-1.5">{children}</dl>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-100 pb-1.5 text-sm">
      <dt className="text-slate-600">{label}</dt>
      <dd className="shrink-0 font-medium tabular-nums text-slate-900">{value}</dd>
    </div>
  );
}
